// Self-contained client for property-service's Ruby Estate admin routes
// (Task 29 Stripe settings — LIVE on dev; Task 33 overview/businesses/fee-plans/fees —
// built in parallel, same contract). Mirrors billingApi.ts / toolsApi.ts: a pure host
// resolver (tested without a DOM), a bare id-token header (no "Bearer " prefix, the
// same convention every sibling admin client in this app uses), and a thin `req`
// wrapper. Lives on the shared `platform` cluster, base path `/estate`, exactly like
// billing-service's `/billing` base path.

export type FeeSource = 'override' | 'deal' | 'default' | 'platform';
export type ConnectStatus = 'none' | 'onboarding' | 'ready';

/**
 * Mirrors property-service `domain/fees.ts` `FeeConfig` — money is integer cents,
 * `achBps`/`cardBps` are basis points (50 = 0.5%). `maxCents: null` = no cap.
 */
export interface FeeConfig {
  achBps: number;
  achFlatCents: number;
  cardBps: number;
  cardFlatCents: number;
  minCents: number;
  maxCents: number | null;
}

export interface FeePlan extends FeeConfig {
  planId: string;
  name: string;
  isDefault?: boolean;
  archived?: boolean;
}

/**
 * Client-side copy of property-service's `computeApplicationFee` (domain/fees.ts) —
 * for the live "On $X rent: fee $Y" example in the Fee plans editor ONLY. The server
 * is the source of truth for every real fee; this never prices a real payment.
 */
export function computeApplicationFee(amountCents: number, method: 'ach' | 'card', cfg: FeeConfig): number {
  if (!Number.isInteger(amountCents) || amountCents <= 0) return 0;
  const bps = method === 'ach' ? cfg.achBps : cfg.cardBps;
  const flat = method === 'ach' ? cfg.achFlatCents : cfg.cardFlatCents;
  let fee = Math.floor((amountCents * bps) / 10_000 + 0.5) + flat;
  fee = Math.max(fee, cfg.minCents);
  if (cfg.maxCents !== null) fee = Math.min(fee, cfg.maxCents);
  return Math.min(fee, amountCents);
}

export interface BizStats {
  tenantId: string;
  businessName: string;
  units: number;
  occupied: number;
  residents: number;
  residentsWithLogin: number;
  activeLeases: number;
  openWorkOrders: number;
  connect: ConnectStatus;
  feePlanId?: string;
  feeSource: FeeSource;
  feeUntil?: string;
  updatedAt: number | string;
  /** This month's figures, when the endpoint returns them alongside stats. */
  volumeCentsThisMonth?: number;
  feeCentsThisMonth?: number;
}

export interface FeesByMonthEntry {
  month: string;
  volumeCents: number;
  feeCents: number;
  count: number;
}

export interface EstateOverview {
  businesses: number;
  units: number;
  occupancyPct: number;
  residents: number;
  residentsWithLogin: number;
  activeLeases: number;
  volumeCentsThisMonth: number;
  feeCentsThisMonth: number;
  feeCentsYtd: number;
  processing: number;
  failedThisMonth: number;
  connect: { none: number; onboarding: number; ready: number };
  openWorkOrders: number;
  trend: FeesByMonthEntry[];
}

export interface FeeRecord {
  sk: string;
  date: string;
  tenantId: string;
  businessName?: string;
  leaseId?: string;
  method: 'ach' | 'card';
  amountCents: number;
  feeCents: number;
  feePlanId?: string;
  feeSource?: FeeSource;
}

export interface FailRecord {
  sk: string;
  date: string;
  tenantId: string;
  businessName?: string;
  reason?: string;
}

export interface BusinessDetail {
  stats: BizStats;
  resolvedFee: { config: FeeConfig; source: FeeSource; planId?: string; until?: string };
  deal?: { estateFeePlanId?: string; estateFeeMonths?: number; name?: string; active?: boolean };
  feeRecords: FeeRecord[];
  failures: FailRecord[];
}

export interface FeesReportTotals {
  volumeCents: number;
  feeCents: number;
  count: number;
}

export interface FeesReport {
  month: string;
  totals: FeesReportTotals;
  byBusiness: Array<{ tenantId: string; businessName?: string } & FeesReportTotals>;
  records?: FeeRecord[];
}

// ---------------------------------------------------------------------------
// Stripe settings (Task 29 — live on dev). Shapes follow adminStripe.ts.
// ---------------------------------------------------------------------------

export type StripeConfigKey = 'secretKey' | 'publishableKey' | 'webhookSecret' | 'feeConfig';

export interface StripeKeyStatus {
  set: boolean;
  tail: string | null;
  mode: 'test' | 'live' | null;
  updatedAt: string | null;
  updatedBy: string | null;
  source: string;
}

export interface StripeWebhookStatus {
  endpointId: string | null;
  status: string | null;
  url: string | null;
  enabledEvents: string[] | null;
}

export interface StripeConfigStatus {
  secretKey: StripeKeyStatus;
  publishableKey: StripeKeyStatus;
  webhookSecret: StripeKeyStatus;
  /** `value` (final review C-I3): the parsed fallback fee config -- not a secret -- when set. */
  feeConfig: StripeKeyStatus & { value?: FeeConfig };
  webhook: StripeWebhookStatus;
}

export type StripeSecretTestResult = 'ok' | 'error' | 'unverified';
export type StripeConnectTestResult = 'ok' | 'not_enabled' | 'error' | 'create_refused' | 'list_only';
export type StripePublishableTestResult = 'ok' | 'missing';
export type StripeWebhookTestResult = 'ok' | 'missing' | 'disabled' | 'url_mismatch';

export interface StripeTestResult {
  secretKey: StripeSecretTestResult;
  connect: StripeConnectTestResult;
  connectHint?: 'permission';
  publishableKey: StripePublishableTestResult;
  webhook: StripeWebhookTestResult;
}

export interface StripeConfigUpdate {
  secretKey?: string;
  publishableKey?: string;
  webhookSecret?: string;
  /** The server's FeeConfig object shape (integer bps / cents, maxCents null = no max). */
  feeConfig?: FeeConfig;
}

export interface StripeWebhookResult extends StripeConfigStatus {
  oldEndpointDeleteFailed?: string;
}

// ---------------------------------------------------------------------------
// Host resolution — same convention as billingApi.ts/toolsApi.ts.
// ---------------------------------------------------------------------------

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveEstateHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/estate-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/estate`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/estate`;
  return `https://platform.api.dev.${apex}/estate`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_ESTATE_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveEstateHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* non-JSON */ }
    throw new Error(detail || `estate-admin ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

/**
 * Same as `req`, but reads the response as plain text — the fees-report CSV
 * export (`format=csv`) is not JSON, and running it through `res.json()` would
 * throw on every successful download.
 */
async function reqText(path: string, init: RequestInit = {}): Promise<string> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* non-JSON */ }
    throw new Error(detail || `estate-admin ${res.status}`);
  }
  return res.text();
}

export const estateAdminApi = {
  // ---- Task 33: overview / businesses / fee plans / fees report ----------
  overview: (month?: string) =>
    req<EstateOverview>(`/admin/overview${month ? `?month=${encodeURIComponent(month)}` : ''}`),

  businesses: () => req<{ items: BizStats[] }>('/admin/businesses'),

  business: (tenantId: string) => req<BusinessDetail>(`/admin/businesses/${encodeURIComponent(tenantId)}`),

  setFeeOverride: (tenantId: string, body: { planId: string | null; until?: string }) =>
    req<BusinessDetail>(`/admin/businesses/${encodeURIComponent(tenantId)}/fee-plan`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  feePlans: () => req<{ items: FeePlan[] }>('/admin/fee-plans'),

  createFeePlan: (plan: Omit<FeePlan, 'planId'> & { planId: string }) =>
    req<FeePlan>('/admin/fee-plans', { method: 'POST', body: JSON.stringify(plan) }),

  updateFeePlan: (planId: string, plan: Partial<Omit<FeePlan, 'planId'>>) =>
    req<FeePlan>(`/admin/fee-plans/${encodeURIComponent(planId)}`, { method: 'PUT', body: JSON.stringify(plan) }),

  feesReportJson: (month: string) =>
    req<FeesReport>(`/admin/fees?month=${encodeURIComponent(month)}&format=json`),

  /** The raw CSV text for `format=csv` — the caller turns it into a download. */
  feesReportCsv: (month: string) =>
    reqText(`/admin/fees?month=${encodeURIComponent(month)}&format=csv`),

  // ---- Task 29: Stripe settings (live on dev) -----------------------------
  stripeConfig: () => req<StripeConfigStatus>('/admin/stripe-config'),

  updateStripeConfig: (update: StripeConfigUpdate) =>
    req<StripeConfigStatus>('/admin/stripe-config', { method: 'PUT', body: JSON.stringify(update) }),

  testStripeConfig: () => req<StripeTestResult>('/admin/stripe-config/test', { method: 'POST' }),

  ensureStripeWebhook: (rotate: boolean) =>
    req<StripeWebhookResult>('/admin/stripe-config/webhook', { method: 'POST', body: JSON.stringify({ rotate }) }),
};
